import { CheckCircle2, ExternalLink, Heart, Share2, Globe, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { addFavorite } from "@/lib/api";
import { formatCompact, formatDate } from "@/lib/format";

export default function ChannelHeader({ stats }) {
  if (!stats) return null;
  const url = `https://www.youtube.com/channel/${stats.id}`;
  const category = (stats.topic_categories || [])[0]?.split("/").pop()?.replace(/_/g, " ");

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const handleFavorite = async () => {
    try {
      await addFavorite({
        channel_id: stats.id,
        title: stats.title,
        thumbnail: stats.thumbnail,
        subscribers: stats.subscribers,
      });
      toast.success("Channel saved to favorites");
    } catch (e) {
      toast.error("Failed to save favorite");
    }
  };

  return (
    <section data-testid="channel-header" className="relative">
      <div className="relative h-40 sm:h-56 w-full overflow-hidden bg-secondary border-b border-border">
        {stats.banner ? (
          <img src={stats.banner} alt={`Banner of channel ${stats.title}`} loading="eager" fetchpriority="high" className="w-full h-full object-cover opacity-70" />
        ) : (
          <div className="w-full h-full grid-pattern noise-bg" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl border-4 border-background bg-card overflow-hidden shrink-0">
            {stats.thumbnail && (
              <img src={stats.thumbnail} alt={`${stats.title} channel avatar`} loading="eager" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 min-w-0 pt-2 sm:pt-8">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="channel-title" className="text-3xl sm:text-4xl font-black tracking-tight">
                {stats.title}
              </h1>
              {stats.verified && (
                <CheckCircle2 className="w-6 h-6 text-primary" data-testid="channel-verified" aria-label="Verified" />
              )}
            </div>
            <p className="mt-3 text-sm text-muted-foreground line-clamp-3 max-w-3xl">
              {stats.description || "No description available."}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              {stats.country && (
                <Badge variant="outline" className="font-mono">
                  <Globe className="w-3 h-3 mr-1" /> {stats.country}
                </Badge>
              )}
              {category && (
                <Badge variant="outline" className="font-mono capitalize">
                  {category}
                </Badge>
              )}
              {stats.published_at && (
                <Badge variant="outline" className="font-mono">
                  <Calendar className="w-3 h-3 mr-1" /> since {formatDate(stats.published_at)}
                </Badge>
              )}
              <Badge className="bg-primary/10 text-primary border border-primary/30 font-mono">
                {formatCompact(stats.subscribers)} subscribers
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:pt-8">
            <Button
              data-testid="favorite-button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={handleFavorite}
            >
              <Heart className="w-4 h-4 mr-1.5" /> Favorite
            </Button>
            <Button
              data-testid="share-button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
            <a href={url} target="_blank" rel="noreferrer" data-testid="channel-external-link">
              <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open on YouTube
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
